<template>
  <div class="stat-chart">
    <canvas ref="canvas" />
  </div>
</template>

<script>
/**
 * Chart displayed in the stats page or in a stat widget.
 * Supposed to illustrate how Shizen has been used for the past few days.
 *
 * @vue-prop {Boolean} [labels]          Display labels on the X-axis
 * @vue-prop {Boolean} [valuesOnChart]   Display values above each point of the chart
 * @vue-prop {Boolean} [type]            One of STAT_CHART_TYPES
 * @vue-prop {Array} [value]             Values of the chart
 */

import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
// Utils
import { STAT_CHART_TYPES, SColors } from '@/utils/constants';

export default {

  name: 'StatChart',

  props: {
    labels: Boolean,
    valuesOnChart: Boolean,
    type: {
      type: String,
      default: STAT_CHART_TYPES[0].type,
      validator: v => STAT_CHART_TYPES.some(t => t.type === v),
    },
    value: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      chart: null,
    };
  },

  computed: {
    /** Array: Labels of the chart */
    chartLabels() {
      return this.value.map(valObj => valObj.label);
    },
    /** Array: Data of the dataset of the chart */
    chartData() {
      return this.value.map(valObj => valObj.value);
    },
    /** Array: Datasets of the chart */
    chartDatasets() {
      return [{
        type: this.type,
        lineTension: 0,
        data: this.chartData,
        borderColor: SColors.statsBlue,
        backgroundColor: this.type === 'line' ? SColors.statsBlueLight : SColors.statsBlue,
        borderWidth: this.type === 'line' ? 2 : 0,
        pointBackgroundColor: SColors.statsBlue,
        pointRadius: 2,
        pointHitRadius: 10,
        pointHoverRadius: 4,
        fill: 'origin',
        maxBarThickness: 8,
        borderRadius: 200,
      }];
    },
  },

  watch: {
    chartDatasets() {
      this.createChart();
    },
    labels() {
      this.createChart();
    },
    valuesOnChart() {
      this.createChart();
    },
  },

  mounted() {
    this.createChart();
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },

  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    /** Make the chart */
    createChart() {
      const { canvas } = this.$refs;
      if (!canvas) {
        return;
      }
      if (this.chart) {
        this.chart.destroy();
      }
      const context = canvas.getContext('2d');
      this.chart = new Chart(context, {
        plugins: [ChartDataLabels],
        data: { datasets: this.chartDatasets, labels: this.chartLabels },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              max: Math.max(...this.chartDatasets[0].data) * 1.1,
              beginAtZero: true,
              grid: {
                color: SColors.grayBgHover,
                borderDash: [3, 3],
                tickColor: 'rgba(0, 0, 0, 0)',
                borderColor: 'rgba(0, 0, 0, 0)',
              },
              ticks: {
                callback: label => (Math.floor(label) === label ? `${label}` : ''),
                color: SColors.gray,
                font: {
                  weight: '600',
                  size: 11,
                },
              },
            },
            x: {
              display: !!this.labels,
              grid: {
                drawTicks: false,
                color: 'transparent',
              },
            },
          },
          plugins: {
            // Label bubble
            datalabels: {
              display: () => this.valuesOnChart,
              formatter: val => (val ? val.sToFixed(2) : val),
              color: SColors.mainTextColor,
              font: { weight: '600', size: '11' },
              anchor: 'end',
              align: 'end',
              clamp: true,
            },
            tooltip: {
              callbacks: {
                title: () => '',
                label: tooltipItem => this.chartLabels[tooltipItem.dataIndex],
              },
            },
          },
        },
      });
    },
  },
};
</script>
